import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardMedia,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from './ConfirmDialog';
import FullImageDialog from './FullImageDialog';
import ImageCropDialog from './ImageCropDialog';
import { fieldLabelSx } from './formFieldStyles';
import {
  ALLOWED_IMAGE_EXTENSIONS,
  MAX_IMAGE_SIZE_MB,
  validateImageFile,
} from '../../utils/imageValidation';
import { resizeImageForUpload } from '../../utils/imageResize';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const ImagePickerField = ({
  label,
  helpText,
  imageUrl,
  onSelect,
  onRemove,
  disabled = false,
  processing = false,
  errorKey = null,
  removeConfirmMessage,
  cropToSquare = false,
  cropAspect = null,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const cropSrcRef = useRef(null);
  const [localErrorKey, setLocalErrorKey] = useState(null);
  const [localErrorParams, setLocalErrorParams] = useState(null);
  const [resizing, setResizing] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropFileName, setCropFileName] = useState('');

  const displayErrorKey = errorKey || localErrorKey;
  const busy = disabled || processing || resizing;
  const cropRatio = cropAspect || (cropToSquare ? 1 : null);

  const revokeCropSrc = () => {
    if (cropSrcRef.current) {
      URL.revokeObjectURL(cropSrcRef.current);
      cropSrcRef.current = null;
    }
  };

  const closeCrop = () => {
    revokeCropSrc();
    setCropSrc(null);
    setCropFileName('');
  };

  const submitFile = async (file) => {
    setResizing(true);
    try {
      const resized = await resizeImageForUpload(file);
      onSelect(resized);
    } finally {
      setResizing(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    const validation = validateImageFile(file);
    if (!validation.valid) {
      setLocalErrorKey(validation.errorKey);
      setLocalErrorParams(validation.errorParams);
      return;
    }

    setLocalErrorKey(null);
    setLocalErrorParams(null);

    if (cropRatio) {
      revokeCropSrc();
      const objectUrl = URL.createObjectURL(file);
      cropSrcRef.current = objectUrl;
      setCropSrc(objectUrl);
      setCropFileName(file.name);
      return;
    }

    await submitFile(file);
  };

  const handleCropApply = async (croppedFile) => {
    closeCrop();
    await submitFile(croppedFile);
  };

  return (
    <Box>
      {label ? (
        <Typography variant="subtitle2" sx={fieldLabelSx}>
          {label}
        </Typography>
      ) : null}
      {helpText ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {helpText}
        </Typography>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_IMAGE_EXTENSIONS.join(',')}
        hidden
        onChange={handleFileChange}
      />

      {imageUrl ? (
        <Card
          sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            width: cropRatio === 1 ? 200 : '100%',
            maxWidth: cropRatio === 1 ? 200 : 360,
            aspectRatio: cropRatio || undefined,
          }}
        >
          <CardMedia
            component="img"
            image={imageUrl}
            alt=""
            onClick={() => setPreviewOpen(true)}
            sx={{
              width: '100%',
              height: cropRatio ? '100%' : 200,
              aspectRatio: cropRatio || undefined,
              objectFit: 'cover',
              cursor: 'pointer',
            }}
          />
          {cropRatio ? (
            <IconButton
              aria-label={label || t('stores.images.addImages')}
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: '#fff',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
              }}
            >
              <CloudUploadIcon />
            </IconButton>
          ) : null}
          <IconButton
            aria-label={t('common.delete')}
            onClick={() => setConfirmRemoveOpen(true)}
            disabled={busy}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: '#fff',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Card>
      ) : (
        <Button
          variant="outlined"
          startIcon={resizing ? <CircularProgress size={18} /> : <CloudUploadIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          sx={{
            textTransform: 'none',
            borderColor: BRAND.accent,
            color: BRAND.accentText,
            '&:hover': {
              borderColor: BRAND.accent,
              backgroundColor: brandAlpha('accent', 0.1),
            },
          }}
        >
          {resizing ? t('stores.images.uploading') : (label || t('stores.images.addImages'))}
        </Button>
      )}

      {displayErrorKey ? (
        <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
          {t(displayErrorKey, localErrorParams || undefined)}
        </Typography>
      ) : null}

      <ConfirmDialog
        open={confirmRemoveOpen}
        onClose={() => setConfirmRemoveOpen(false)}
        onConfirm={() => {
          setConfirmRemoveOpen(false);
          onRemove();
        }}
        title={t('common.confirm')}
        message={removeConfirmMessage || t('stores.images.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        confirmColor="error"
        imageUrl={imageUrl}
      />

      <FullImageDialog
        open={previewOpen}
        imageUrl={imageUrl}
        onClose={() => setPreviewOpen(false)}
      />

      <ImageCropDialog
        open={Boolean(cropSrc)}
        imageSrc={cropSrc}
        fileName={cropFileName}
        onClose={closeCrop}
        onApply={handleCropApply}
        aspect={cropRatio || 1}
      />
    </Box>
  );
};

export default ImagePickerField;
